import { apiAction, API_ERROR, API_SUCCESS } from "../dux/api";
import { apiCallComplete, apiCallStart } from "../dux/apiStatus";
import { openSnackbar } from "../dux/snackbar";
import {
  getPaymentTestUrl,
  getPaymentPollStatusUrl,
  getPaymentCheckBookingUrl,
  getPaymentStatusUrl,
  getPaymentReconciliationReportUrl,
  getPaymentReconciliationRunUrl,
  getPaymentReconciliationBookingDetailUrl,
} from "../helpers/apiEndPoints";
import {
  PAYMENT_RECONCILIATION_REPORT,
  PAYMENT_RECONCILIATION_RUN,
  PAYMENT_RECONCILIATION_BOOKING_DETAIL,
} from "../dux/paymentReconciliation";
import {
  appendQueryToUrl,
  buildReconciliationQueryParams,
  buildReconciliationRunBody,
  buildBookingDetailQueryParams,
} from "../helpers/paymentReconciliationHelper";

// Action types for payment operations
export const PAYMENT_TEST = "PAYMENT_TEST";
export const PAYMENT_POLL_STATUS = "PAYMENT_POLL_STATUS";
export const PAYMENT_CHECK_BOOKING = "PAYMENT_CHECK_BOOKING";
export const PAYMENT_STATUS = "PAYMENT_STATUS";

const payment = () => (next) => (action) => {
  next(action);

  // Test payment endpoint
  if (action.type === PAYMENT_TEST) {
    next(apiCallStart({ api: "paymentTest" }));
    next(
      apiAction({
        url: getPaymentTestUrl(),
        feature: PAYMENT_TEST,
        method: "GET",
      })
    );
  }

  if (action.type === `${PAYMENT_TEST} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "paymentTest" }));
    next(openSnackbar({ message: "Payment service is working" }));
  }

  if (action.type === `${PAYMENT_TEST} ${API_ERROR}`) {
    next(apiCallComplete({ api: "paymentTest" }));
    next(
      openSnackbar({
        message: "Payment service is not responding",
        type: "error",
      })
    );
  }

  // Manual payment status polling
  if (action.type === PAYMENT_POLL_STATUS) {
    next(apiCallStart({ api: "paymentPollStatus" }));
    next(
      apiAction({
        url: getPaymentPollStatusUrl(),
        feature: PAYMENT_POLL_STATUS,
        method: "POST",
      })
    );
  }

  if (action.type === `${PAYMENT_POLL_STATUS} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "paymentPollStatus" }));
    next(openSnackbar({ message: "Payment status polling completed" }));
  }

  if (action.type === `${PAYMENT_POLL_STATUS} ${API_ERROR}`) {
    next(apiCallComplete({ api: "paymentPollStatus" }));
    next(
      openSnackbar({ message: "Failed to poll payment status", type: "error" })
    );
  }

  // Check payment status for specific booking
  if (action.type === PAYMENT_CHECK_BOOKING) {
    next(apiCallStart({ api: "paymentCheckBooking" }));
    next(
      apiAction({
        url: getPaymentCheckBookingUrl(action.payload.bookingId),
        feature: PAYMENT_CHECK_BOOKING,
        method: "GET",
      })
    );
  }

  if (action.type === `${PAYMENT_CHECK_BOOKING} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "paymentCheckBooking" }));
    next(openSnackbar({ message: "Payment status checked successfully" }));
  }

  if (action.type === `${PAYMENT_CHECK_BOOKING} ${API_ERROR}`) {
    next(apiCallComplete({ api: "paymentCheckBooking" }));
    next(
      openSnackbar({ message: "Failed to check payment status", type: "error" })
    );
  }

  // Get payment service status
  if (action.type === PAYMENT_STATUS) {
    next(apiCallStart({ api: "paymentStatus" }));
    next(
      apiAction({
        url: getPaymentStatusUrl(),
        feature: PAYMENT_STATUS,
        method: "GET",
      })
    );
  }

  if (action.type === `${PAYMENT_STATUS} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "paymentStatus" }));
  }

  if (action.type === `${PAYMENT_STATUS} ${API_ERROR}`) {
    next(apiCallComplete({ api: "paymentStatus" }));
    next(
      openSnackbar({
        message: "Failed to get payment service status",
        type: "error",
      })
    );
  }

  // Payment reconciliation (read-only report)
  if (action.type === PAYMENT_RECONCILIATION_REPORT) {
    const query = buildReconciliationQueryParams(action.payload || {});
    next(apiCallStart({ api: "paymentReconciliationReport" }));
    next(
      apiAction({
        url: getPaymentReconciliationReportUrl(),
        feature: PAYMENT_RECONCILIATION_REPORT,
        method: "GET",
        data: query,
      })
    );
  }

  if (
    action.type === `${PAYMENT_RECONCILIATION_REPORT} ${API_SUCCESS}` ||
    action.type === `${PAYMENT_RECONCILIATION_REPORT} ${API_ERROR}`
  ) {
    next(apiCallComplete({ api: "paymentReconciliationReport" }));
  }

  // Payment reconciliation run (POST; remediate: true)
  if (action.type === PAYMENT_RECONCILIATION_RUN) {
    const query = buildReconciliationQueryParams(action.payload || {});
    const url = appendQueryToUrl(getPaymentReconciliationRunUrl(), query);
    next(apiCallStart({ api: "paymentReconciliationRun" }));
    next(
      apiAction({
        url,
        feature: PAYMENT_RECONCILIATION_RUN,
        method: "POST",
        data: buildReconciliationRunBody(action.payload || {}),
      })
    );
  }

  if (
    action.type === `${PAYMENT_RECONCILIATION_RUN} ${API_SUCCESS}` ||
    action.type === `${PAYMENT_RECONCILIATION_RUN} ${API_ERROR}`
  ) {
    next(apiCallComplete({ api: "paymentReconciliationRun" }));
  }

  if (action.type === PAYMENT_RECONCILIATION_BOOKING_DETAIL) {
    const { bookingId, ...flags } = action.payload || {};
    const id =
      bookingId != null && String(bookingId).trim() !== ""
        ? String(bookingId).trim()
        : null;
    if (!id) {
      next(apiCallComplete({ api: "paymentReconciliationBookingDetail" }));
      return;
    }
    const query = buildBookingDetailQueryParams(flags || {});
    const url = appendQueryToUrl(
      getPaymentReconciliationBookingDetailUrl(id),
      query
    );
    next(apiCallStart({ api: "paymentReconciliationBookingDetail" }));
    next(
      apiAction({
        url,
        feature: PAYMENT_RECONCILIATION_BOOKING_DETAIL,
        method: "GET",
      })
    );
  }

  if (
    action.type === `${PAYMENT_RECONCILIATION_BOOKING_DETAIL} ${API_SUCCESS}` ||
    action.type === `${PAYMENT_RECONCILIATION_BOOKING_DETAIL} ${API_ERROR}`
  ) {
    next(apiCallComplete({ api: "paymentReconciliationBookingDetail" }));
  }
};

export default payment;
